// src/Components/Common/AutoBreadCrumb.tsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

interface BreadcrumbData {
  title: string;
  link: string;
}

interface AutoBreadCrumbProps {
  customTitle?: string; // Optional override for the left side title
  customBreadcrumbs?: BreadcrumbData[]; // Optional override for breadcrumbs
  contractorData?: any; // Add this for contractor-specific pages
}

const AutoBreadCrumb: React.FC<AutoBreadCrumbProps> = ({ 
  customTitle,
  customBreadcrumbs,
  contractorData // Add this line
}) => {
  
  // Enhanced formatting for segment names
  const formatSegmentName = (segment: string, position: number, allSegments: string[]): string => {
    // Handle special route segments
    if (segment === 'categories') return 'Categories';
    if (segment === 'locations') return 'Locations';
    
    // Check if it's a state code (2 letters)
    if (segment.length === 2 && /^[a-z]{2}$/i.test(segment)) {
      return segment.toUpperCase();
    }
    
    // Handle special category names for better SEO
    const specialCases: { [key: string]: string } = {
      'hvac': 'HVAC',
      'ac-repair': 'AC Repair',
      'home-improvement': 'Home Improvement',
      'lawn-care': 'Lawn Care',
      'tree-service': 'Tree Service',
      'pest-control': 'Pest Control',
      'home-security': 'Home Security',
      'solar-panels': 'Solar Panels',
      'web-design': 'Web Design',
      'seo-services': 'SEO Services',
    };

    const lowerSlug = segment.toLowerCase();
    if (specialCases[lowerSlug]) {
      return specialCases[lowerSlug];
    }

    // Default formatting
    return segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Auto-generate breadcrumbs based on URL
  const generateBreadcrumbsFromUrl = (): { breadcrumbs: BreadcrumbData[], pageTitle: string } => {
    const path = window.location.pathname;
    const segments = path.split('/').filter(segment => segment !== '');
    const breadcrumbs: BreadcrumbData[] = [];
    let pageTitle = 'Home';
    
    // Handle contractor routes specifically
    if (segments[0] === 'contractors') {
      // Add "All Contractors" as the base
      breadcrumbs.push({ title: 'All Contractors', link: '/contractors' });
      
      // Process remaining segments
      let currentPath = '/contractors';
      
      for (let i = 1; i < segments.length; i++) {
        const segment = segments[i];
        currentPath += `/${segment}`;
        
        // Use enhanced formatting
        const displayName = formatSegmentName(segment, i, segments);
        
        // Add to breadcrumbs (except for the last segment)
        if (i < segments.length - 1) {
          breadcrumbs.push({ title: displayName, link: currentPath });
        }
        
        // Set page title to the last segment
        if (i === segments.length - 1) {
          pageTitle = displayName;
        }
      }
      
      // Special case: if we only have /contractors, adjust the title
      if (segments.length === 1) {
        pageTitle = 'All Contractors';
        breadcrumbs.pop(); // Remove the duplicate breadcrumb
      }
      
    } else {
      // Handle non-contractor routes
      let currentPath = '';
      
      segments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const displayName = formatSegmentName(segment, index, segments);
        
        // Add to breadcrumbs (except for the last segment)
        if (index < segments.length - 1) {
          breadcrumbs.push({ title: displayName, link: currentPath });
        }
        
        // Set page title to the last segment
        if (index === segments.length - 1) {
          pageTitle = displayName;
        }
      });
    }
    
    return { breadcrumbs, pageTitle };
  };
  
  // Generate breadcrumbs and page title
  const { breadcrumbs: autoBreadcrumbs, pageTitle: autoPageTitle } = generateBreadcrumbsFromUrl();
  
  // Use provided values or auto-generated ones
  const effectiveBreadcrumbs = customBreadcrumbs || autoBreadcrumbs;
  const effectiveTitle = customTitle || autoPageTitle;

  // Generate breadcrumb structured data for SEO
  useEffect(() => {
    if (effectiveBreadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: effectiveBreadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.title,
          item: `${window.location.origin}${crumb.link}`
        }))
      };

      // Add current page as final breadcrumb
      breadcrumbSchema.itemListElement.push({
        '@type': 'ListItem',
        position: effectiveBreadcrumbs.length + 1,
        name: effectiveTitle,
        item: window.location.href
      });

      // Remove existing breadcrumb schema
      const existingSchema = document.querySelector('script[type="application/ld+json"][data-breadcrumb="true"]');
      if (existingSchema) {
        existingSchema.remove();
      }

      // Add new breadcrumb schema
      const scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-breadcrumb', 'true');
      scriptTag.textContent = JSON.stringify(breadcrumbSchema, null, 2);
      document.head.appendChild(scriptTag);
    }
  }, [effectiveBreadcrumbs, effectiveTitle]);
  
  return (
    <React.Fragment>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            {/* Changed to h1 for better SEO while keeping h4 styling */}
            <h1 className="mb-sm-0 h4">{effectiveTitle}</h1>

            <div className="page-title-right">
              {/* Added accessibility attributes */}
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb m-0">
                  {effectiveBreadcrumbs.map((crumb, index) => (
                    <li key={index} className="breadcrumb-item">
                      <Link 
                        to={crumb.link}
                        className="text-decoration-none"
                        title={`Go to ${crumb.title}`}
                      >
                        {crumb.title}
                      </Link>
                    </li>
                  ))}
                  <li className="breadcrumb-item active" aria-current="page">
                    {effectiveTitle}
                  </li>
                </ol>
              </nav>
            </div>

          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default AutoBreadCrumb;